import { useState, useEffect, useCallback } from 'react';
import {
    List,
    useListContext,
    useGetList,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    TextField as MuiTextField,
    InputAdornment,
    Typography,
    alpha,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgentOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLessOutlined';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

interface UserRecord {
    id: string;
    email: string;
    userName: string;
    displayName: string;
    avatarInitial: string;
    emailConfirmed: boolean;
    isLocked: boolean;
    isSystemAccount: boolean;
    roles: string[];
    adminRoles: string[];
    status: string;
    primaryAdminRole: string | null;
    createdAt: string | null;
    lastLoginAt: string | null;
}

interface DeviceAccess {
    nodeId: string;
    nodeName: string | null;
    role: string;
}

interface KpiCardProps {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
}

const KpiCard = ({ title, value, icon, color }: KpiCardProps) => (
    <Card
        sx={{
            height: '100%',
            background: 'white',
            borderRadius: '12px',
            border: `1px solid ${colors.slate[100]}`,
            transition: 'all 0.2s',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transform: 'translateY(-1px)',
            },
        }}
    >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: alpha(color, 0.1),
                        color: color,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: colors.slate[900], lineHeight: 1 }}
                    >
                        {value}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: colors.slate[500], fontSize: 13, mt: 0.5 }}
                    >
                        {title}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const getAvatarGradient = (primaryRole: string | null) => {
    switch (primaryRole) {
        case 'TechSupport':
            return 'linear-gradient(135deg, #0891b2, #0d9488)';
        case 'Administrator':
            return 'linear-gradient(135deg, #d97706, #b45309)';
        default:
            return 'linear-gradient(135deg, #7c3aed, #6d28d9)';
    }
};

const UserAvatar = ({ user }: { user: UserRecord }) => (
    <Box
        sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: getAvatarGradient(user.primaryAdminRole),
            color: 'white',
            fontWeight: 600,
            fontSize: 16,
            flexShrink: 0,
        }}
    >
        {user.avatarInitial}
    </Box>
);

const StatusChip = ({ user }: { user: UserRecord }) => {
    if (user.isLocked) {
        return (
            <Chip
                label="Locked"
                size="small"
                icon={<LockIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error }}
            />
        );
    }
    if (!user.emailConfirmed) {
        return (
            <Chip
                label="Unconfirmed"
                size="small"
                icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: alpha(colors.warning, 0.1), color: colors.warning }}
            />
        );
    }
    return (
        <Chip
            label="Active"
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success }}
        />
    );
};

const AdminRoleDisplay = ({ user }: { user: UserRecord }) => {
    if (!user.adminRoles || user.adminRoles.length === 0) {
        return (
            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic', fontSize: 13 }}>
                Customer
            </Typography>
        );
    }

    const primaryRole = user.adminRoles[0];
    const roleColor = primaryRole === 'TechSupport' ? colors.primary : colors.warning;
    const RoleIcon = primaryRole === 'TechSupport' ? SupportAgentIcon : AdminPanelSettingsIcon;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <RoleIcon sx={{ fontSize: 16, color: roleColor }} />
            <Typography sx={{ color: roleColor, fontWeight: 500, fontSize: 13 }}>
                {primaryRole}
            </Typography>
            {user.adminRoles.length > 1 && (
                <Tooltip title={user.adminRoles.join(', ')}>
                    <Typography
                        sx={{
                            color: colors.slate[400],
                            fontSize: 11,
                            fontWeight: 500,
                            ml: 0.5,
                        }}
                    >
                        +{user.adminRoles.length - 1}
                    </Typography>
                </Tooltip>
            )}
        </Box>
    );
};

const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
};

// Device access sub-row
interface DeviceRowProps {
    userId: string;
    devices: DeviceAccess[];
}

const DeviceSubRow = ({ devices }: DeviceRowProps) => {
    const navigate = useNavigate();

    return (
        <TableRow sx={{ bgcolor: 'transparent', '& td': { border: 'none', pt: 0, pb: 1 } }}>
            <TableCell colSpan={8} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 6 }}>
                    {devices.map((device) => (
                        <Box
                            key={device.nodeId}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '140px 70px 1fr 60px',
                                alignItems: 'center',
                                gap: 2,
                                py: 0.75,
                                px: 1.5,
                                borderRadius: '6px',
                                bgcolor: alpha(colors.slate[100], 0.5),
                                borderLeft: `3px solid ${device.role === 'Owner' ? colors.primary : colors.slate[400]}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DevicesIcon sx={{ fontSize: 14, color: colors.slate[400], flexShrink: 0 }} />
                                <Typography
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: colors.slate[700],
                                    }}
                                >
                                    {device.nodeId}
                                </Typography>
                            </Box>
                            <Chip
                                label={device.role}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    bgcolor: device.role === 'Owner' ? alpha(colors.primary, 0.1) : alpha(colors.slate[400], 0.1),
                                    color: device.role === 'Owner' ? colors.primary : colors.slate[500],
                                }}
                            />
                            <Typography
                                sx={{
                                    color: colors.slate[500],
                                    fontSize: 12,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {device.nodeName || '—'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Tooltip title="View Device">
                                    <IconButton
                                        size="small"
                                        onClick={() => navigate(`/devices/${device.nodeId}/show`)}
                                        sx={{
                                            border: `1px solid ${colors.slate[200]}`,
                                            borderRadius: '6px',
                                            p: 0.5,
                                            '&:hover': {
                                                bgcolor: colors.slate[100],
                                                borderColor: colors.slate[300],
                                            },
                                        }}
                                    >
                                        <DevicesIcon sx={{ fontSize: 14, color: colors.slate[500] }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </TableCell>
        </TableRow>
    );
};

const UserListHeader = () => {
    const { filterValues, setFilters } = useListContext();
    const [search, setSearch] = useState(filterValues.q || '');

    // Derive filter from filterValues, default to 'all'
    const currentFilter = filterValues.filter || 'all';

    // Get all users for KPI calculation
    const { data: allUsers } = useGetList<UserRecord>('users', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });

    const users = allUsers || [];
    const totalCount = users.length;
    const activeCount = users.filter(u => !u.isLocked && u.emailConfirmed).length;
    const lockedCount = users.filter(u => u.isLocked).length;
    const techSupportCount = users.filter(u => u.adminRoles?.includes('TechSupport')).length;

    // Debounced search - only update q, preserve other filters
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search !== (filterValues.q || '')) {
                setFilters({ ...filterValues, q: search || undefined });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search, filterValues, setFilters]);

    const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: string | null) => {
        if (newFilter && newFilter !== currentFilter) {
            // Set filter, preserve search
            setFilters({
                q: filterValues.q,
                filter: newFilter === 'all' ? undefined : newFilter,
            });
        }
    };

    return (
        <Box mb={3}>
            {/* Page Header */}
            <Box mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                    User Management
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.5 }}>
                    View and manage user accounts
                </Typography>
            </Box>

            {/* KPI Summary Cards */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 2,
                    mb: 3,
                }}
            >
                <KpiCard
                    title="Active Users"
                    value={activeCount}
                    icon={<CheckCircleIcon />}
                    color={colors.success}
                />
                <KpiCard
                    title="Total Users"
                    value={totalCount}
                    icon={<PeopleIcon />}
                    color={colors.primary}
                />
                <KpiCard
                    title="Tech Support"
                    value={techSupportCount}
                    icon={<SupportAgentIcon />}
                    color="#0891b2"
                />
                <KpiCard
                    title="Locked"
                    value={lockedCount}
                    icon={<LockIcon />}
                    color={colors.error}
                />
            </Box>

            {/* Filter & Search Controls */}
            <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}` }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: colors.slate[600] }}
                        >
                            Show:
                        </Typography>
                        <ToggleButtonGroup
                            value={currentFilter}
                            exclusive
                            onChange={handleFilterChange}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 2,
                                    py: 1,
                                    borderRadius: '8px !important',
                                    border: 'none',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: 13,
                                    color: colors.slate[600],
                                    '&.Mui-selected': {
                                        bgcolor: 'white',
                                        color: colors.primary,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                    },
                                },
                                bgcolor: colors.slate[100],
                                borderRadius: '10px',
                                p: 0.5,
                            }}
                        >
                            <ToggleButton value="all">
                                <PeopleIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                All Users
                            </ToggleButton>
                            <ToggleButton value="active">
                                <CheckCircleIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                Active Only
                            </ToggleButton>
                            <ToggleButton value="techsupport">
                                <SupportAgentIcon sx={{ fontSize: 16, mr: 0.75 }} />
                                Tech Support
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <MuiTextField
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by email or name..."
                            size="small"
                            sx={{
                                ml: 2,
                                width: 280,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: 14,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                label={`${totalCount} user(s)`}
                                size="small"
                                sx={{
                                    bgcolor: colors.slate[100],
                                    color: colors.slate[500],
                                    fontWeight: 500,
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

// Custom data grid that supports device sub-rows
const UserDataGrid = () => {
    const { data, isLoading } = useListContext<UserRecord>();
    const navigate = useNavigate();
    const [deviceAccess, setDeviceAccess] = useState<Record<string, DeviceAccess[]>>({});
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

    // Fetch device access when users change
    const fetchDeviceAccess = useCallback(async (users: UserRecord[]) => {
        // Only fetch for non-system users
        const regularUsers = users.filter(u => !u.isSystemAccount);
        if (regularUsers.length === 0) {
            setDeviceAccess({});
            return;
        }

        try {
            const response = await fetch('/api/admin/users/devices', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userIds: regularUsers.map(u => u.id),
                }),
            });

            if (response.ok) {
                const accessData = await response.json();
                setDeviceAccess(accessData);
            }
        } catch (error) {
            console.error('Failed to fetch device access:', error);
        }
    }, []);

    useEffect(() => {
        if (data && data.length > 0) {
            fetchDeviceAccess(data);
        }
    }, [data, fetchDeviceAccess]);

    const toggleExpand = (userId: string) => {
        setExpandedUsers(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: colors.slate[500] }}>
                Loading users...
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)}, ${alpha(colors.secondary, 0.1)})`,
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                    }}
                >
                    <PeopleIcon sx={{ fontSize: 36, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: colors.slate[900], mb: 1 }}>
                    No Users Found
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500] }}>
                    No users match your current filters.
                </Typography>
            </Box>
        );
    }

    return (
        <Table>
            <TableHead>
                <TableRow
                    sx={{
                        '& th': {
                            bgcolor: colors.slate[50],
                            fontWeight: 600,
                            color: colors.slate[600],
                            borderBottom: `1px solid ${colors.slate[200]}`,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1.5,
                        },
                    }}
                >
                    <TableCell width={40}></TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((user) => {
                    const devices = deviceAccess[user.id] || [];
                    const hasDevices = devices.length > 0;
                    const isExpanded = expandedUsers.has(user.id);
                    const isInactive = user.isLocked;
                    const isStaleLogin = user.lastLoginAt
                        ? new Date().getTime() - new Date(user.lastLoginAt).getTime() > 30 * 24 * 60 * 60 * 1000
                        : true;

                    return [
                        // User row
                        <TableRow
                            key={user.id}
                            sx={{
                                opacity: isInactive ? 0.6 : 1,
                                '&:hover': { bgcolor: colors.slate[50] },
                                '& td': { py: 1.5 },
                            }}
                        >
                            <TableCell>
                                {hasDevices && (
                                    <IconButton
                                        size="small"
                                        onClick={() => toggleExpand(user.id)}
                                        sx={{ p: 0.5 }}
                                    >
                                        {isExpanded ? (
                                            <ExpandLessIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                                        )}
                                    </IconButton>
                                )}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <UserAvatar user={user} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: isInactive ? colors.slate[500] : colors.slate[900],
                                            }}
                                        >
                                            {user.displayName}
                                        </Typography>
                                        {user.isSystemAccount && (
                                            <Chip
                                                label="System"
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    bgcolor: colors.slate[100],
                                                    color: colors.slate[400],
                                                    width: 'fit-content',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                        sx={{
                                            color: isInactive ? colors.slate[400] : colors.slate[600],
                                            fontSize: 13,
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                    {!user.emailConfirmed && !user.isLocked && (
                                        <Tooltip title="Email not verified">
                                            <ErrorIcon sx={{ fontSize: 14, color: colors.warning }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <AdminRoleDisplay user={user} />
                            </TableCell>
                            <TableCell>
                                <StatusChip user={user} />
                            </TableCell>
                            <TableCell>
                                <Typography
                                    sx={{ fontSize: 13, color: colors.slate[600] }}
                                    title={user.createdAt ? new Date(user.createdAt).toLocaleString() : undefined}
                                >
                                    {formatDate(user.createdAt)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: !user.lastLoginAt
                                            ? colors.slate[400]
                                            : isStaleLogin
                                                ? colors.warning
                                                : colors.slate[600],
                                        fontStyle: !user.lastLoginAt ? 'italic' : 'normal',
                                    }}
                                    title={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : undefined}
                                >
                                    {formatRelativeTime(user.lastLoginAt)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Tooltip title="Edit User">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate(`/users/${user.id}`)}
                                            sx={{
                                                border: `1px solid ${colors.slate[200]}`,
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    bgcolor: colors.slate[50],
                                                    borderColor: colors.slate[300],
                                                },
                                            }}
                                        >
                                            <EditIcon sx={{ fontSize: 18, color: colors.slate[600] }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>,
                        // Device access sub-rows (collapsible)
                        hasDevices && isExpanded && (
                            <DeviceSubRow
                                key={`${user.id}-devices`}
                                userId={user.id}
                                devices={devices}
                            />
                        ),
                    ];
                })}
            </TableBody>
        </Table>
    );
};

export const UserList = () => (
    <List
        actions={false}
        component="div"
        sx={{
            '& .RaList-main': { mt: 0 },
        }}
    >
        <UserListHeader />
        <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}`, overflow: 'hidden' }}>
            <UserDataGrid />
        </Card>
    </List>
);
